// src/hooks/pages/useAccountOverview.ts
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@/store';
import {
  selectOrganizationData,
  selectOrgError,
  selectOrgLoadingAny,
  selectOrgLoadingProfile,
  selectOrgLoading,
} from '@/store/selectors/organization';
import { fetchOrganizationDashboard } from '@/store/thunks/organization/fetchOrganizationDashboard';
import { fetchOrgProfile } from '@/store/thunks/organization/fetchOrgProfile';
import { getSessionToken } from '@/utils/sessionStorage';
import { decodeJWT } from '@/utils/jwtDecoder';

type TokenPayload = {
  sub?: string;             // sometimes used for org id in your app
  organizationId?: string;  // preferred key if present
};

export function useAccountOverview() {
  const dispatch = useAppDispatch();

  const data           = useSelector(selectOrganizationData);   // can be null initially
  const error          = useSelector(selectOrgError);
  const busyAny        = useSelector(selectOrgLoadingAny);
  const loadingProfile = useSelector(selectOrgLoadingProfile);
  const loading        = useSelector(selectOrgLoading);

  // --- read orgId from JWT once ---
  const organizationId = useMemo(() => {
    try {
      const token = getSessionToken() ?? '';
      if (!token) return '';
      const { payload } = decodeJWT<TokenPayload>(token);
      // prefer organizationId, fallback to sub
      const maybe =
        (typeof payload.organizationId === 'string' && payload.organizationId) ||
        (typeof payload.sub === 'string' && payload.sub) ||
        '';
      return maybe;
    } catch {
      return '';
    }
  }, []);

  // derived: do we need profile?
  const profileMissing = useMemo(() => {
    const hasCustomerSince = Boolean(data?.customerSince);
    const hasName = (data?.name?.length ?? 0) > 0;
    return !(hasCustomerSince && hasName);
  }, [data?.customerSince, data?.name]);

  // fire-once guards
  const triedDashboardRef = useRef(false);
  const triedProfileRef   = useRef(false);

  // ---- Bootstrap dashboard ONCE when we have an organizationId ----
  useEffect(() => {
    if (triedDashboardRef.current) return;
    if (!organizationId) return;

    // if you already have admins & users, skip bootstrap call
    const hasAdmins = (data?.administrators?.length ?? 0) > 0;
    const hasUsers  = (data?.organizationUsers?.length ?? 0) > 0;
    if (hasAdmins && hasUsers) {
      triedDashboardRef.current = true;
      return;
    }

    // avoid while loading
    if (loading.admins || loading.orgUsers || loading.profile || loading.seats) return;

    triedDashboardRef.current = true;
    void dispatch(fetchOrganizationDashboard({ id: organizationId }));
  }, [
    organizationId,
    data?.administrators?.length,
    data?.organizationUsers?.length,
    loading.admins,
    loading.orgUsers,
    loading.profile,
    loading.seats,
    dispatch,
  ]);

  // ---- Top-up profile ONCE if missing ----
  useEffect(() => {
    if (triedProfileRef.current) return;
    if (organizationId.length === 0) return;
    if (!profileMissing) return;
    if (loadingProfile) return;

    triedProfileRef.current = true;
    void dispatch(fetchOrgProfile({ id: organizationId }));
  }, [organizationId, profileMissing, loadingProfile, dispatch]);

  return {
    data,
    busyAny,
    loadingProfile,
    error,
  };
}
